import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export function ProfileTab() {
  const [p, setP] = useState(null);
  const load = () => api.get("/my/profile").then((r) => setP({ ...r.data, rolesText: (r.data.roles || []).join(", ") }));
  useEffect(() => { load(); }, []);
  if (!p) return null;
  const save = () => api.put("/my/profile", { name: p.name, phone: p.phone, professional_title: p.professional_title, summary: p.summary,
    roles: p.rolesText.split(",").map((s) => s.trim()).filter(Boolean) }).then(() => { toast.success("Profile saved"); load(); });
  const f = (k, l) => <div><Label>{l}</Label><Input value={p[k] || ""} onChange={(e) => setP({ ...p, [k]: e.target.value })} data-testid={`my-profile-${k}`} /></div>;
  return (
    <div className="rounded-2xl border bg-white p-4 space-y-3" data-testid="my-profile">
      <p className="text-xs text-slate-500">Your portable CaterCTRL profile. It belongs to you — employers cannot edit it.</p>
      <div className="grid md:grid-cols-2 gap-3">{f("name", "Name")}{f("phone", "Contact phone")}{f("professional_title", "Professional title")}{f("rolesText", "Roles I work in (comma separated)")}</div>
      <div><Label>Professional summary</Label><textarea className="w-full border rounded-lg p-2 text-sm" rows={3} value={p.summary} onChange={(e) => setP({ ...p, summary: e.target.value })} data-testid="my-profile-summary" /></div>
      <div className="text-xs text-slate-500 flex gap-4 flex-wrap" data-testid="my-profile-summary-counts">
        <span>{p.email}</span><span>Freelance: {p.available_for_freelance ? "available" : "not available"}</span>
        <span>{p.training_count} qualifications</span><span>{p.active_engagements} active engagements</span></div>
      <Button className="bg-brand hover:bg-brand-hover" onClick={save} data-testid="my-profile-save">Save profile</Button>
    </div>
  );
}

const PROV = { "self-declared": "Self-declared", "organisation-recorded": "Recorded by employer", "organisation-verified": "Verified by employer" };

export function TrainingTab() {
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ title: "", issuer: "", issue_date: "", expiry_date: "" });
  const load = () => api.get("/my/training").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const add = () => api.post("/my/training", f).then(() => { setF({ title: "", issuer: "", issue_date: "", expiry_date: "" }); load(); }).catch((e) => toast.error(e.response?.data?.detail || "Failed"));
  return (
    <div className="space-y-3" data-testid="my-training">
      <div className="rounded-2xl border bg-white divide-y">
        {rows.length === 0 && <p className="p-4 text-sm text-slate-400">No qualifications yet.</p>}
        {rows.map((r) => <div key={r.id} className="p-3 text-sm flex justify-between flex-wrap gap-2" data-testid={`my-training-${r.id}`}>
          <span><strong>{r.title}</strong> {r.issuer && <span className="text-slate-500">· {r.issuer}</span>}<span className="block text-xs text-slate-400">{r.issue_date || "—"} → {r.expiry_date || "no expiry"} · {r.status}</span></span>
          <span className="text-xs self-center"><span className="px-2 py-0.5 rounded bg-slate-100" data-testid={`my-training-prov-${r.id}`}>{PROV[r.provenance]}</span> <span className="text-slate-400">· not verified by CaterCTRL</span>
            {r.source === "personal" && <button className="text-red-500 ml-2" onClick={() => api.delete(`/my/training/${r.id}`).then(load)} data-testid={`my-training-del-${r.id}`}>Remove</button>}</span>
        </div>)}
      </div>
      <div className="rounded-2xl border bg-white p-4 grid md:grid-cols-5 gap-2 items-end">
        {[["title", "Qualification", "text"], ["issuer", "Issuer", "text"], ["issue_date", "Issued", "date"], ["expiry_date", "Expires", "date"]].map(([k, l, t]) =>
          <div key={k}><Label>{l}</Label><Input type={t} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`my-training-${k}`} /></div>)}
        <Button onClick={add} disabled={!f.title} data-testid="my-training-add">Add</Button>
      </div>
    </div>
  );
}

export function DocumentsTab() {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/my/documents").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const upload = (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Max 5 MB"); return; }
    const rd = new FileReader();
    rd.onload = () => api.post("/my/documents", { filename: file.name, content_type: file.type, data_b64: String(rd.result).split(",")[1], title: file.name })
      .then(() => { toast.success("Uploaded"); load(); }).catch((e) => toast.error(e.response?.data?.detail || "Upload failed"));
    rd.readAsDataURL(file);
  };
  const open = (d) => api.get(`/my/documents/${d.document_id}/file`, { responseType: "blob" }).then((r) => window.open(URL.createObjectURL(r.data), "_blank"));
  return (
    <div className="space-y-3" data-testid="my-documents">
      <p className="text-xs text-slate-500">Personal documents you keep wherever you work. Workplace documents stay with your workplace.</p>
      <input type="file" accept="application/pdf,image/jpeg,image/png,image/webp" onChange={(e) => upload(e.target.files[0])} data-testid="my-documents-upload" />
      <div className="rounded-2xl border bg-white divide-y">
        {rows.length === 0 && <p className="p-4 text-sm text-slate-400">No documents yet.</p>}
        {rows.map((d) => <div key={d.document_id} className="p-3 text-sm flex justify-between" data-testid={`my-document-${d.document_id}`}>
          <button className="text-left text-brand" onClick={() => open(d)} data-testid={`my-document-open-${d.document_id}`}>{d.filename}</button>
          <button className="text-red-500 text-xs" onClick={() => api.delete(`/my/documents/${d.document_id}`).then(load)} data-testid={`my-document-del-${d.document_id}`}>Remove</button></div>)}
      </div>
    </div>
  );
}
